import type {EventId} from '@cohort/shared/apps';
import {AppIdSchema, EventIdSchema, MediaKindSchema} from '@cohort/shared/apps';
import {asArray, DatetimeSchema, LocalizedStringSchema} from '@cohort/shared/schema/common';
import {DateRangeSchema} from '@cohort/shared/schema/common/dates';
import {PaginationParamsSchema} from '@cohort/shared/schema/common/pagination';
import {PerkTypeSchema} from '@cohort/shared/schema/common/perks';
import {UserEventsOrderBySchema} from '@cohort/shared/schema/common/userEvent';
import {z} from 'zod';

export const UserEventASchema = z.object({
  id: z.string().uuid(),
  eventId: EventIdSchema,
  properties: z.unknown(),
  createdAt: DatetimeSchema,
  campaignInternalName: z.string().nullable(),
  badgeInternalName: z.string().nullable(),
  perkInternalName: z.string().nullable(),
  digitalAssetCollectionInternalName: z.string().nullable(),
  cohortName: z.string().nullable(),
  isResourceDeleted: z.boolean(),
  eventDate: DatetimeSchema,
});
export type UserEventADto = z.infer<typeof UserEventASchema>;

export const ListUserEventsParamsASchema = PaginationParamsSchema.extend({
  userId: z.string().uuid(),
  eventIds: z.preprocess(asArray, z.array(EventIdSchema).optional()),
  search: z.string().optional(),
  orderBy: UserEventsOrderBySchema.default('eventDate'),
});
export type ListUserEventsParamsADto = z.infer<typeof ListUserEventsParamsASchema>;

export const ExportUserEventsParamsASchema = z.object({
  dateRange: DateRangeSchema.optional(),
  eventKinds: z.preprocess(asArray, z.array(EventIdSchema)).optional(),
});
export type ExportUserEventsParamsADto = z.infer<typeof ExportUserEventsParamsASchema>;
export type ExportUserEventsParamsInput = z.input<typeof ExportUserEventsParamsASchema>;

export const UserEventGroupASchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('app'),
    appId: AppIdSchema,
  }),
  z.object({
    type: z.literal('badge'),
    internalName: z.string(),
    imageFileKey: z.string().nullable(),
    deleted: z.boolean(),
  }),
  z.object({
    type: z.literal('challenge'),
    internalName: z.string(),
    imageFileKey: z.string().nullable(),
    deleted: z.boolean(),
    status: z.enum(['not-participating', 'in-progress', 'completed']),
  }),
  z.object({
    type: z.literal('cohort'),
    name: z.string(),
    deleted: z.boolean(),
  }),
  z.object({
    type: z.literal('content'),
    internalName: z.string(),
    media: z.object({
      config: z.any(),
      kind: MediaKindSchema,
    }),
    deleted: z.boolean(),
  }),
  z.object({
    type: z.literal('digital-asset'),
    internalName: z.string(),
    imageFileKey: z.string(),
  }),
  z.object({
    type: z.literal('perk-access'),
    internalName: z.string(),
    imageFileKey: z.string().nullable(),
    perkType: PerkTypeSchema,
  }),
  z.object({
    type: z.literal('reward-box'),
    title: LocalizedStringSchema,
    imageFileKey: z.string().nullable(),
  }),
  z.object({
    type: z.literal('user'),
  }),
]);
export type UserEventGroupADto = z.infer<typeof UserEventGroupASchema>;

export const UserEventGroupEventsASchema = z.object({
  group: UserEventGroupASchema,
  events: z.array(UserEventASchema),
});
export type UserEventGroupEventsADto = z.infer<typeof UserEventGroupEventsASchema>;

// dev: changes to this list should be accompanied by a UserEventGroupView migration
export const HIDDEN_EVENTS: EventId[] = [
  'cohort.content.available',
  'cohort.challenge.new-challenge-available',
  'cohort.challenge.new-step-available',
  'cohort.reward-box.unlocked',
];
